import React, { useState, useEffect } from 'react';
import { Wifi, MessageSquare, Mic, PhoneOff, AlertTriangle, Monitor, BatteryCharging, Battery, BatteryLow, BatteryWarning, Lightbulb, Brain, CheckCircle, Gavel, VolumeX, Info, Plus, RefreshCw, Loader2, Target, ArrowRight, MinusCircle, HelpCircle, Library } from 'lucide-react';
import { AVATARS, AvatarDefinition } from '../types';
import { listDevicesViaN8n, assignDeviceViaN8n, unassignDeviceViaN8n, DeviceInfo } from '../services/n8n';
import { QuickCreateAvatarModal } from './QuickCreateAvatarModal';
import { AddAvatarFromCatalogModal } from './AddAvatarFromCatalogModal';
import { AskQuestionModal } from './live-session/AskQuestionModal';
import { StartVoiceConversationModal } from './live-session/StartVoiceConversationModal';
import { useBrowserVoiceAgent } from '../hooks/useBrowserVoiceAgent';
import { ConversationProvider } from '@elevenlabs/react';
import { useAuth } from '../contexts/AuthContext';
import { syncProjectAvatarsIntoSession } from '../services/avatarCatalogService';

const formatRole = (role: string) => role.replace(/_/g, ' ');

function getBatteryLevelClass(batteryLevel: number): string {
  if (batteryLevel > 75) return 'text-green-600';
  if (batteryLevel > 25) return 'text-slate-500';
  return 'text-red-500';
}

export const RESPONSE_MODES = [
  { value: 'open_ideas', label: 'Open Ideas', icon: Lightbulb },
  { value: 'structured_brainstorm', label: 'Structured', icon: Brain },
  { value: 'solution_focused', label: 'Solution', icon: CheckCircle },
  { value: 'closing_down', label: 'Closing Down', icon: Target },
  { value: 'next_steps', label: 'Next Steps', icon: ArrowRight },
  { value: 'critique', label: 'Critique', icon: Gavel },
  { value: 'silent', label: 'Silent', icon: VolumeX },
  { value: 'blank', label: 'Blank', icon: MinusCircle },
];

interface PhysicalDevice {
  deviceId: string;
  batteryLevel: number;
  isCharging: boolean;
  currentAssignment?: string;
  lastSeen?: string;
}

interface AvatarCardsPanelProps {
  sessionId: string;
  avatarContributions?: Record<string, number>;
  pendingAvatars?: string[];
  speakingAvatar?: string | null;
  sessionAvatars?: any[];
  responseMode?: string;
  onResponseModeChange?: (mode: string) => void;
  onRequestAvatar?: (role: string) => void;
  loading?: boolean;
  projectId?: string;
  onAvatarCreated?: (avatar: AvatarDefinition) => void;
  onAskQuestion?: (question: string, avatarRoles: string[], maxWords: number | undefined, responseMode: string, skipGraph: boolean) => void;
  converseProvider?: 'off' | 'elevenlabs';
}

const BatteryIcon = ({ device }: { device: PhysicalDevice }) => {
  if (device.isCharging) return <BatteryCharging size={16} className="text-green-600" />;
  if (device.batteryLevel > 75) return <Battery size={16} className="text-green-600" />;
  if (device.batteryLevel > 25) return <BatteryWarning size={16} className="text-slate-500" />;
  return <BatteryLow size={16} className="text-red-500" />;
};

const AvatarCardsPanelInner: React.FC<AvatarCardsPanelProps> = ({
  sessionId,
  avatarContributions = {},
  pendingAvatars = [],
  speakingAvatar = null,
  sessionAvatars = [],
  responseMode = 'open_ideas',
  onResponseModeChange,
  onRequestAvatar,
  loading = false,
  projectId,
  onAvatarCreated,
  onAskQuestion,
  converseProvider = 'elevenlabs',
}) => {
  const [devices, setDevices] = useState<PhysicalDevice[]>([]);
  const [deviceAssignments, setDeviceAssignments] = useState<Record<string, string>>({});
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showAddFromCatalog, setShowAddFromCatalog] = useState(false);
  const [showAskQuestion, setShowAskQuestion] = useState(false);
  const [talkTo, setTalkTo] = useState<{ role: string; name: string } | null>(null);
  const { can } = useAuth();
  const voice = useBrowserVoiceAgent(sessionId);
  // A visible button that always 403s is worse than no button.
  const canTalk = can('Integrations::UseBrowserVoice') && converseProvider !== 'off';
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [requestingAvatar, setRequestingAvatar] = useState<string | null>(null);

  const handleSyncProjectAvatars = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const result = await syncProjectAvatarsIntoSession(sessionId);
      if (result.added.length > 0) {
        setSyncMessage(`Added ${result.added.length} avatar${result.added.length > 1 ? 's' : ''}: ${result.added.map(a => a.name).join(', ')}`);
        // Notify the parent once to trigger a real refetch of session avatars —
        // it re-fetches from the server rather than reading this argument.
        onAvatarCreated?.({} as AvatarDefinition);
      } else {
        setSyncMessage('All enabled project avatars are already in this session.');
      }
      setTimeout(() => setSyncMessage(''), 4000);
    } catch {
      setSyncMessage('Sync failed — please try again.');
      setTimeout(() => setSyncMessage(''), 4000);
    } finally {
      setSyncing(false);
    }
  };

  const avatarsToDisplay = sessionAvatars.length > 0 ? sessionAvatars : Object.values(AVATARS);

  useEffect(() => {
    loadDevices();
    const interval = setInterval(loadDevices, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { if (!loading) setRequestingAvatar(null); }, [loading]);

  const loadDevices = async () => {
    try {
      const response = await listDevicesViaN8n();
      const mapped: PhysicalDevice[] = (response.devices || []).map((d: DeviceInfo) => ({
        deviceId: d.deviceId,
        batteryLevel: d.batteryLevel ?? -1,
        isCharging: d.isCharging ?? false,
        currentAssignment: d.assignedRole || undefined,
        lastSeen: d.lastHeartbeat
      }));
      setDevices(mapped);

      const assignments: Record<string, string> = {};
      (response.devices || []).forEach((d: DeviceInfo) => {
        if (d.assignedRole) {
          assignments[d.assignedRole] = d.deviceId;
        }
      });
      setDeviceAssignments(assignments);
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const handleDeviceChange = async (avatarRole: string, deviceId: string, avatarId?: string) => {
    try {
      if (!deviceId) {
        const currentDeviceId = deviceAssignments[avatarRole];
        if (currentDeviceId) {
          await unassignDeviceViaN8n(currentDeviceId);
          await loadDevices();
        }
        return;
      }
      // The avatar's id pairs the device to this avatar even when another avatar shares its role string
      await assignDeviceViaN8n(deviceId, avatarRole, sessionId, undefined, typeof avatarId === 'string' && avatarId ? avatarId : undefined);
      await loadDevices();
    } catch (error) {
      console.error('Device assignment error:', error);
    }
  };

  const getAvatarStatus = (avatarRole: string): 'speaking' | 'thinking' | 'idle' | 'pending' | 'listening' => {
    if (speakingAvatar === avatarRole) return 'speaking';
    if (pendingAvatars.includes(avatarRole)) return 'pending';
    return 'listening';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'speaking':
        return (
          <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Speaking
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Pending
          </div>
        );
      case 'thinking':
        return (
          <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            Thinking
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            Listening
          </div>
        );
    }
  };

  const getDeviceOptions = (avatarRole: string) => {
    return devices.filter(device =>
      !device.currentAssignment || device.currentAssignment === avatarRole
    );
  };

  // Compute stats
  const activeCount = avatarsToDisplay.length;
  const totalContributions = Object.values(avatarContributions).reduce((sum, c) => sum + c, 0);

  // Find avatar with least contributions for "Needs Attention"
  const avatarEntries = avatarsToDisplay.map(a => ({
    name: a.name,
    role: a.role,
    contributions: avatarContributions[a.role] || 0,
  }));
  const leastActive = avatarEntries.length > 0
    ? avatarEntries.reduce((min, a) => a.contributions < min.contributions ? a : min, avatarEntries[0])
    : null;

  const currentModeConfig = RESPONSE_MODES.find(m => m.value === responseMode);

  const getModeDescription = (mode: string) => {
    switch (mode) {
      case 'open_ideas': return 'Avatars will generate divergent concepts freely without constraints. Encourages volume of ideas.';
      case 'structured_brainstorm': return 'Avatars follow a structured brainstorming approach with defined steps and categories.';
      case 'solution_focused': return 'Avatars focus on practical, implementable solutions rather than ideation.';
      case 'critique': return 'Avatars analyze proposals critically, identifying risks, gaps, and potential improvements.';
      case 'closing_down': return 'Avatars help narrow options to concrete decisions. Focus on prioritisation and synthesis.';
      case 'next_steps': return 'Avatars focus on actionable next steps, owners, and timelines to move forward.';
      case 'silent': return 'Avatars are paused and will not generate contributions until a different mode is selected.';
      case 'blank': return 'Avatars produce no output. Use to pause all responses without changing session mode.';
      default: return '';
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 pb-32">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Active Avatars</h1>
            <p className="text-slate-500">Manage your AI facilitators for the current session.</p>
          </div>
          <div className="flex items-center gap-2">
            {onAskQuestion && (
              <button
                onClick={() => setShowAskQuestion(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                <HelpCircle size={16} />
                Ask a Question
              </button>
            )}
            {projectId && (
              <>
                <button
                  onClick={handleSyncProjectAvatars}
                  disabled={syncing}
                  title="Sync enabled project avatars into this session"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  Sync Avatars
                </button>
                <button
                  onClick={() => setShowAddFromCatalog(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  <Library size={16} />
                  Add from Catalog
                </button>
                <button
                  onClick={() => setShowQuickCreate(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
                >
                  <Plus size={16} />
                  Add Avatar
                </button>
              </>
            )}
          </div>
          {syncMessage && (
            <p className="text-xs text-slate-500 mt-1 text-right">{syncMessage}</p>
          )}
        </div>

        {/* Ask a Question Modal */}
        {talkTo && (
        <StartVoiceConversationModal
          avatarName={talkTo.name}
          onStart={(opts) => voice.start(talkTo.role, opts)}
          onClose={() => setTalkTo(null)}
        />
      )}

      {voice.error && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm rounded-xl bg-red-50 border border-red-200 px-4 py-3 shadow-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-700">{voice.error}</p>
        </div>
      )}

      {showAskQuestion && onAskQuestion && (
          <AskQuestionModal
            avatars={avatarsToDisplay.map(a => ({ name: a.name, role: a.role }))}
            defaultResponseMode={responseMode}
            onSubmit={onAskQuestion}
            onClose={() => setShowAskQuestion(false)}
          />
        )}

        {/* Quick Create Modal */}
        {showQuickCreate && projectId && (
          <QuickCreateAvatarModal
            mode="manual"
            projectId={projectId}
            sessionId={sessionId}
            onClose={() => setShowQuickCreate(false)}
            onCreated={(avatar) => {
              setShowQuickCreate(false);
              onAvatarCreated?.(avatar);
            }}
          />
        )}

        {/* Add from Catalog Modal */}
        {showAddFromCatalog && (
          <AddAvatarFromCatalogModal
            sessionId={sessionId}
            existingAvatarDefinitionIds={avatarsToDisplay.map(a => a.avatarDefinitionId).filter(Boolean)}
            onClose={() => setShowAddFromCatalog(false)}
            onAdded={(avatar) => {
              onAvatarCreated?.(avatar);
            }}
          />
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Active</p>
              <p className="text-2xl font-bold text-slate-900">{activeCount}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg text-green-600">
              <Wifi size={24} />
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Contributions</p>
              <p className="text-2xl font-bold text-slate-900">{totalContributions}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
              <MessageSquare size={24} />
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Devices</p>
              <p className="text-2xl font-bold text-slate-900">{devices.length}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
              <Monitor size={24} />
            </div>
          </div>
          {leastActive && (
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between border-l-4 border-l-amber-500">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-xs font-medium text-slate-500 uppercase">Least Active</p>
                <p className="text-lg font-bold text-slate-900 truncate">{leastActive.name}</p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                <AlertTriangle size={24} />
              </div>
            </div>
          )}
        </div>

        {/* Avatar Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {avatarsToDisplay.map((avatar, index) => {
            const avatarName = avatar.name;
            const avatarEmoji = avatar.emoji || '🤖';
            const roleKey = avatar.role || `avatar_${index}`;
            const imageUrl = avatar.imageUrl;
            const primaryColor = avatar.primaryColor;
            const secondaryColor = avatar.secondaryColor;
            const contributionCount = avatarContributions[roleKey] || 0;
            const status = getAvatarStatus(roleKey);
            const isRequesting = requestingAvatar === roleKey || status === 'pending' || status === 'thinking';
            const assignedDeviceId = deviceAssignments[roleKey];
            const assignedDevice = devices.find(d => d.deviceId === assignedDeviceId);

            // TODO: acceptance rate calculation not yet implemented — placeholder until wired up
            const acceptanceRate = '—';

            return (
              <div
                key={avatarName || index}
                className={`group bg-white rounded-xl border shadow-sm hover:shadow-md transition-all p-5 flex flex-col items-center relative overflow-hidden ${
                  status === 'speaking' ? 'ring-2 ring-primary ring-offset-2' : 'border-slate-200'
                }`}
                style={primaryColor ? { borderTopWidth: '3px', borderTopColor: primaryColor } : undefined}
              >
                {/* Top accent bar for speaking avatars */}
                {status === 'speaking' && !primaryColor && (
                  <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
                )}

                {/* Status badge */}
                <div className="absolute top-3 right-3">
                  {getStatusBadge(status)}
                </div>

                {/* Avatar circle */}
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center text-4xl shadow-inner overflow-hidden mb-4"
                  style={{
                    ...(secondaryColor ? { backgroundColor: secondaryColor } : { background: 'linear-gradient(to top right, #f1f5f9, #f8fafc)' }),
                    ...(status === 'pending' ? {
                      '--glow-color': (primaryColor || '#00c7c4') + '66',
                      animation: 'avatar-glow 2s ease-in-out infinite',
                    } as React.CSSProperties : {}),
                  }}
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt={avatarName} className="w-20 h-20 object-cover rounded-full" />
                  ) : (
                    <span className="text-5xl">{avatarEmoji}</span>
                  )}
                </div>

                {/* Name & Role */}
                <h3 className="text-lg font-bold text-slate-900">{avatarName}</h3>
                <p className="text-sm font-medium mb-4 capitalize line-clamp-1" style={primaryColor ? { color: primaryColor } : undefined}>
                  {avatar.description || formatRole(roleKey)}
                </p>

                {/* Stats */}
                <div className="w-full grid grid-cols-2 gap-2 mb-4">
                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                    <span className="block text-xl font-bold text-slate-700">{contributionCount}</span>
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Contribs</span>
                  </div>
                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                    <span className="block text-xl font-bold text-slate-700">{acceptanceRate}</span>
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Accepted</span>
                  </div>
                </div>

                {/* Talk — browser voice conversation with this avatar */}
                {canTalk && (
                  voice.avatarRole === roleKey ? (
                    <button
                      onClick={() => voice.stop()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all bg-red-500 text-white hover:bg-red-600 active:scale-95 mb-2"
                    >
                      <PhoneOff size={14} />
                      {voice.status === 'connecting' ? 'Connecting…' : 'End conversation'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setTalkTo({ role: roleKey, name: avatarName })}
                      disabled={voice.isActive}
                      title={voice.isActive ? 'Already talking to another avatar' : undefined}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all bg-primary/10 text-primary hover:bg-primary/20 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed mb-2"
                    >
                      <Mic size={14} />
                      Talk
                    </button>
                  )
                )}

                {/* Request Response Button */}
                {onRequestAvatar && (
                  <button
                    onClick={() => { setRequestingAvatar(roleKey); onRequestAvatar(roleKey); }}
                    disabled={isRequesting}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all bg-primary/10 text-primary hover:bg-primary/20 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed mb-2"
                  >
                    <MessageSquare size={14} />
                    {isRequesting ? 'Thinking...' : 'Request Response'}
                  </button>
                )}

                {/* Device footer */}
                <div className="w-full mt-auto pt-4 border-t border-slate-100 flex justify-between items-center">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Monitor size={16} />
                    <select
                      value={assignedDeviceId || ''}
                      onChange={(e) => handleDeviceChange(roleKey, e.target.value, avatar.id)}
                      className="bg-transparent border-none text-xs text-slate-500 focus:ring-0 p-0 cursor-pointer"
                    >
                      <option value="">No device</option>
                      {getDeviceOptions(roleKey).map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.deviceId}
                        </option>
                      ))}
                    </select>
                  </div>
                  {assignedDevice && (
                    <div className="flex items-center gap-1 text-xs font-medium">
                      <BatteryIcon device={assignedDevice} />
                      <span className={getBatteryLevelClass(assignedDevice.batteryLevel)}>
                        {assignedDevice.batteryLevel}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Response Mode Section */}
        {onResponseModeChange && (
          <div className="mt-8">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary-dark">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Response Mode</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {RESPONSE_MODES.map(mode => {
                    const Icon = mode.icon;
                    const isActive = responseMode === mode.value;
                    return (
                      <button
                        key={mode.value}
                        onClick={() => onResponseModeChange(mode.value)}
                        className={`flex-1 min-w-[140px] py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all active:scale-95 ${
                          isActive
                            ? 'bg-navy text-white shadow-md border border-navy'
                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Icon size={18} />
                        {mode.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="p-6 flex gap-4 items-start">
                <Info size={20} className="text-primary-dark mt-1 shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Current Mode: {currentModeConfig?.label || 'Open Ideas'}
                  </h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {getModeDescription(responseMode)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarCardsPanel;

/**
 * The ElevenLabs SDK keeps conversation state in React context, so the panel
 * cannot call useBrowserVoiceAgent without a provider above it. Wrapping here
 * rather than at the call site means that can never be forgotten.
 */
export const AvatarCardsPanel: React.FC<AvatarCardsPanelProps> = (props) => (
  <ConversationProvider>
    <AvatarCardsPanelInner {...props} />
  </ConversationProvider>
);
